import * as React from "react";
import {GridColDef} from "@mui/x-data-grid";
import {formatTime, getLastStr, stringAvatar} from "@/helper/method";
import {Avatar, ButtonGroup, Rating} from "@mui/material";
import Stack from "@mui/material/Stack";
import {CustomerPO} from "@/model/customer";
import {WriteCustomer} from "@/pages/Customer/WriteCustomer";
import Chip from "@mui/material/Chip";
import {BtnType, ProTable} from "@/widget/ProTable/ProTable";
import {ViewCustomer} from "@/pages/Customer/ViewCustomer";
import {CustomerPageVO, useFreedCustomers, useQueryCustomerPage, useTransferCustomers} from "@/service/customer.s";
import {AccountBalanceOutlined, NavigateBefore, NavigateNext} from "@mui/icons-material";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import {OverridableStringUnion} from "@mui/types";
import {ChipPropsColorOverrides} from "@mui/material/Chip/Chip";
import {defaultPageParams, permScope} from "@/helper/constant";
import {useGlobalActions} from "@/store/global.c";
import {TransferForm, WriteTransferForm} from "@/widget/TransferForm/TransferForm";
import {useRoleUsers} from "@/store/user.c";
import {BaseVO} from "@/model/global";
import {useAuthInfo} from "@/store/account.c";

export const CustomerListPage = () => {
    const {
        mutate: customerPageMutate,
        data: customerPageData,
        isPending: customerPagePending,
    } = useQueryCustomerPage()
    const {mutateAsync: freedCustomersMutate} = useFreedCustomers()
    const {mutateAsync: transferCustomersMutate} = useTransferCustomers()
    const {setToast, setDialogModal} = useGlobalActions()
    const users = useRoleUsers(["manager", "marketing"])
    const {role: {dataScope}} = useAuthInfo()

    const columns: GridColDef<CustomerPO>[] = [
        {
            field: "customerName",
            headerName: "客户名称",
            flex: 1.3,
            minWidth: 150
        },
        {
            field: "creator",
            headerName: "负责人",
            headerAlign: "center",
            align: "center",
            flex: 0.6,
            display: 'flex',
            renderCell: ({row}) => <Avatar {...stringAvatar(row.creator)} />
        },
        {
            field: "contact",
            headerName: "联系人",
            headerAlign: "center",
            align: "center",
            valueFormatter: value => value || "-"
        },
        {
            field: "mobile",
            headerName: "联系电话",
            headerAlign: "center",
            align: "center",
            flex: 1,
            valueFormatter: value => value || "-"
        },
        {
            field: "status",
            headerName: "客户状态",
            flex: 0.5,
            renderCell: ({value}) => {
                const colors: { [index: string]: 'success' | 'info' } = {
                    "成交": 'success',
                    "意向": 'info',
                }
                return <Chip label={value} variant={'outlined'} color={colors[value]} size="small"/>;
            }
        },
        {
            field: "location",
            headerName: "所在区域",
            headerAlign: "center",
            align: "center",
            flex: 0.8,
            renderCell: ({row}) => getLastStr(row.location),
        },
        {
            field: "level",
            flex: 1,
            align: 'center',
            headerAlign: "center",
            headerName: "客户级别",
            renderCell: ({row}) => {
                return (
                    <Stack
                        direction="row"
                        spacing={1}
                        justifyContent={'center'}
                        alignItems={"center"}
                        height={"100%"}
                    >
                        <Rating
                            size={"medium"}
                            readOnly={true}
                            value={row.level}
                            name="simple-controlled"
                        />
                    </Stack>
                );
            },
        },
        {
            field: "updatedAt",
            headerName: "最后跟进时间",
            headerAlign: "right",
            align: "right",
            // valueFormatter: (value) => formatTime("D", value),
            flex: 0.7,
            display: 'flex',
            renderCell: ({row}) => {
                let release: React.ReactNode = <></>
                const {label, color} = calcFollowDate(row.updatedAt);
                if (!isEmpty(label) && !isEmpty(color)) {
                    release = <Chip sx={{maxWidth: 68, maxHeight: 18}} size={'small'} color={color} variant={'outlined'}
                                    label={<Typography fontSize={8.5}>{label}</Typography>}/>
                }
                return <Stack justifyContent={'center'} alignContent={'center'} alignItems={'center'} justifyItems={'center'}> <Typography
                    component="div">{formatTime("D", row.updatedAt)}</Typography>
                    {release}
                </Stack>
            }
        },
    ];

    const calcFollowDate = (updatedAt: Date) => {
        const updatedDate = dayjs(updatedAt)
        const currentDate = dayjs();
        const diffDay = currentDate.diff(updatedDate, 'day')
        const subDay = 180 - diffDay
        let label: string = ""
        let color: OverridableStringUnion<'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning', ChipPropsColorOverrides> = 'default'
        if (subDay < 0) {
            label = '可回收'
            color = 'error'

        } else if (subDay > 0 && subDay < 30) {
            color = 'warning'
            label = `${subDay}天后回收`
        }
        return {label, color}
    }

    const submitTransferCallback = async (flag: number, keys: number[], onSearch?: (params: any) => void, values?: WriteTransferForm) => {
        const result: BaseVO = await transferCustomersMutate({flag, customerIds: keys, userId: values?.userId, username: values?.username})
        if (result.code == 0) {
            onSearch && onSearch(defaultPageParams)
            setDialogModal({open: false})
            setToast({message: result.msg, open: true, severity: 'success'})
        } else {
            setToast({message: result.msg, open: true, severity: 'error'})
        }
    }
    const btnCallback = async ({btn, onSearch, keys, rows}: BtnType<CustomerPageVO>) => {
        if (btn.code == "customer_change_owner") {
            if (keys.length <= 10 && keys.length > 0) {
                const content: string = rows.map((row: CustomerPageVO) => row.customerName)?.join(",")
                setDialogModal({
                    header: btn.menuName,
                    maxWidth: 'xs',
                    body: <TransferForm content={content} submitCallback={(values: WriteTransferForm) => submitTransferCallback(1, keys, onSearch, values)} users={users}
                                        ids={keys}/>,
                    open: true,
                    onSearch
                })
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
        if (btn.code == 'customer_freed') {
            if (keys.length <= 10 && keys.length > 0) {
                const result = await freedCustomersMutate({customerIds: keys})
                if (result.code == 0) {
                    onSearch && onSearch(defaultPageParams)
                    setToast({message: result.msg, open: true, severity: 'success'})
                } else {
                    setToast({message: result.msg, open: true, severity: 'error'})
                }
            } else {
                setToast({message: "请选择1至10条数据", open: true, severity: 'warning'})
            }
            return
        }
    }

    return (
        <ProTable<CustomerPageVO>
            loading={customerPagePending}
            columnVisibilityModel={{creator: dataScope !== permScope['self']}}
            btnCallback={btnCallback}
            renderDrawer={{view: <ViewCustomer mode={'drawer'}/>, write: <WriteCustomer/>}}
            drawerProps={{
                view: {
                    bodyWidth: 600, headerHeight: 68, hiddenFooter: true, viewBtnCallback: (row: CustomerPageVO) => {
                        return {
                            custom: true,
                            title: row.customerName,
                            businessId: row.id,
                            switchButton: <ButtonGroup size={'small'} sx={{
                                "& .MuiButton-startIcon": {marginRight: 0},
                                "& .MuiButton-endIcon": {marginLeft: 0}
                                , "& .MuiButtonGroup-grouped": {minWidth: 20, height: 20, width: 12, bgcolor: '#f2f4fb',}
                            }} variant="outlined">
                                <Button size={'small'} startIcon={<NavigateBefore/>}/>
                                <Button size={'small'} endIcon={<NavigateNext/>}/>
                            </ButtonGroup>,
                            icon: <AccountBalanceOutlined color={'info'} fontSize={'small'}/>,
                            tags: <Stack direction={'row'} spacing={1.4} alignContent={'center'}>
                                <Chip size={'small'} variant={'outlined'} sx={{height: 16}} color={'warning'} label={<Typography fontSize={10}>{row.status}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'error'}
                                      label={<Typography fontSize={10}>{calcFollowDate(row.updatedAt)?.label || formatTime("D", row.updatedAt)}</Typography>}/>
                                <Chip sx={{height: 16}} size={'small'} variant={'outlined'} color={'success'} label={<Typography fontSize={10}>多次成交</Typography>}/>
                            </Stack>, instanceId: row.instanceId
                        }
                    }
                }
            }}
            columns={columns}
            data={customerPageData?.data}
            onSearch={(params) => {
                customerPageMutate({status: 0, params});
            }}
            code={"customer"}
        />
    );
};
